import axios from 'axios';
import { loaderService } from './loaderService';

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

let activeRequests = 0;

const showLoader = () => {
  if (activeRequests === 0) {
    loaderService.showLoader();
  }
  activeRequests++;
};

const hideLoader = () => {
  activeRequests--;
  if (activeRequests <= 0) {
    activeRequests = 0;
    loaderService.hideLoader();
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) =>
    error ? prom.reject(error) : prom.resolve(token),
  );
  failedQueue = [];
};

const isPublicAuthRequest = (url = '') =>
  url.includes('/auth/login') || url.includes('/auth/refresh');

api.interceptors.request.use(
  (config) => {
    if (!config.skipGlobalLoader) {
      showLoader();
    }

    const token = localStorage.getItem('token');
    if (token && !isPublicAuthRequest(config.url || '')) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    if (!error.config?.skipGlobalLoader) {
      hideLoader();
    }
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    if (!response.config?.skipGlobalLoader) {
      hideLoader();
    }
    return response;
  },
  async (error) => {
    try {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !isPublicAuthRequest(originalRequest.url || '')
      ) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          clearAuth();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/auth/refresh`,
            { refreshToken },
          );

          const newAccessToken = response.data.payload.token;

          localStorage.setItem('token', newAccessToken);
          api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (err) {
          processQueue(err, null);
          clearAuth();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      if (
        error.response?.status === 403 &&
        error.response?.data?.code === 'MUST_CHANGE_PASSWORD'
      ) {
        try {
          const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
          storedUser.mustChangePassword = true;
          localStorage.setItem('userData', JSON.stringify(storedUser));
        } catch (storageError) {
          /* el formulario de cambio se muestra al recargar */
        }
        if (!window.location.pathname.startsWith('/admin')) {
          window.location.assign('/admin/default');
        } else if (!window.__forcingPasswordChange) {
          window.__forcingPasswordChange = true;
          window.location.reload();
        }
      }

      return Promise.reject(error);
    } finally {
      if (!error.config?.skipGlobalLoader) {
        hideLoader();
      }
    }
  },
);

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isAuthenticated');
  sessionStorage.setItem('sessionExpired', 'true');
  window.location.href = '/auth/sign-in';
}

export default api;
